import { Box, Button, HStack, Text, Checkbox, Input, Menu, useToast } from "@chakra-ui/react";
import { CaretRight, Check, MagicWand, Upload } from "@phosphor-icons/react";
import LocalSpinner from "components/AppSpinner/AppSpinner";
import DataTableV2 from "components/DataTable/DateTableV2";
import React, { useEffect, useState } from "react";
import { colors } from "theme/foundations/colors";
import { formatDate, showErrorToast } from "utils";
import { useRightSidebar } from "context/RightSidebarContext";
import SourceDetailsPanel from "./SourceDetailsRSidebar";
import { getFileType, getFolderForRound, KB_FOLDER } from "api/common/fileinfo";
import { useQuery, useQueryClient } from "react-query";
import { queryKey } from "api/api-utils";
import fileinfoService, { apiListUsage } from "api/common/fileinfo.service";
import { H2 } from "components/text/headings";
import FileDownloadButton from "components/FileDownloadButton";
import SourcesUploadRSidebar from "./SourcesUploadRSidebar";
import { useSelector } from "react-redux";
import useAirUserRoles from "utils/AuthUtils";
import { useNavigate } from "react-router-dom";
import { routes } from "config/paths";
import { gotoFilteredQuestionList } from "../Dashboard/components/AIDashboard/AIDashboard";
import { MultiSelect } from "../../../components";
import questionnaireService from "../../../api/issb/questionnaire.service";
import { issbKeys } from "../../../hooks/query";
import { useAuth } from "../../../context";
import { Card } from "../../../components/Card";
import SourcesRoundsAsSourceSelector from "./SourcesRoundsAsSourceSelector";

const SELECTED_COLUMN_ID = "selected";

/**
 * File upload and management panel
 * Design: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1099-20369&t=ANW7dTdV9oBTWOQ3-0
 *
 * @param {React.ReactNode} HackBelow - A component to render below the main content. Provided
 * as a quick-hack to render the onboarding nav buttons
 * @param {boolean} showFiles - Whether to show the files list. Another quick-hack for the onboarding screens
 *
 * @param {string[]} hideColumns - An array of column ids to hide
 * @param {string[]} selectedIds - An array of file ids to select. If unset, dont show checkboxes. 
 * This could also act to mark  the AI Review path vs the Sources setup path.
 * @param {string[]} selectedRounds - An array of round ids to select. If unset, use current-round.sourceRoundIds
 * @param {function} setSelectedRounds - A function to set the selected rounds. If unset, modify current-round.sourceRoundIds and save edits
 * @param {string[]} tables - An array of table names to show: ACTIVE, OTHER, ARCHIVED,KB. If unset, show all tables
 */
export default function SourcesListPanel({
  HackBelow,
  showFiles,
  hideColumns,
  hideAIReviewButton,
  selectedIds,
  setSelectedIds,
  sourceRoundIds,
  setSourceRoundIds,
  tables,
}) {
  const { isController } = useAirUserRoles();

  if (!tables) {
    tables = ["ACTIVE"];
    if (isController()) {
      tables.push("OTHER");
      tables.push("ARCHIVED");
    }
  }

  const { setRightSidebar, doOpen } = useRightSidebar();
  // Get the list of files
  // Note: useQuery seems to need the () => fn() wrapper function on fn
  const { data: filesPage, isLoading } = useQuery("fileinfoService.list", () => fileinfoService.list());
  let files = filesPage?.content;
  // Get the list of archived files
  const { data: filesArchivedPage, isLoading: isLoadingArchived } = useQuery(["fileinfoService.list", "archived"], () =>
    fileinfoService.list({ query: "archived:true" })
  );
  // KB files?
  const { data: filesKBPage, isLoading: isLoadingKB } = useQuery(
    ["fileinfoService.list", KB_FOLDER],
    () => fileinfoService.list({ query: "folder:" + KB_FOLDER }),
    {
      enabled: tables?.includes(KB_FOLDER),
    }
  );

  // TODO support deep linking to a file details
  // const [searchParams, setSearchParams] = useSearchParams();
  const roundSelector = useSelector((state) => state.issbRound);
  const roundId = roundSelector?.selectedRound?.id;
  const folder = tables.includes(KB_FOLDER) && tables.length === 1 ? KB_FOLDER : getFolderForRound(roundId);

  const openSidebar = (item) => {
    setRightSidebar(<SourceDetailsPanel item={item} folder={folder} />);
    doOpen();
  };

  // Get the usage of each file
  const { data: fileUsages } = useQuery(queryKey("apiListUsage"), () => apiListUsage(files?.map((f) => f.id)), {
    enabled: !!files?.length, // don't fetch until we have a list of files
  });
  const usageByFileinfoId = {};
  if (fileUsages) {
    for (const usage of fileUsages) {
      if (!usageByFileinfoId[usage.fileinfoId]) {
        usageByFileinfoId[usage.fileinfoId] = [];
      }
      if (!usageByFileinfoId[usage.fileinfoId].includes(usage.usageId)) {
        usageByFileinfoId[usage.fileinfoId].push(usage.usageId);
      }
    }
  }
  const gotoUsage = (fileinfo) => {
    console.log("gotoUsage", fileinfo);
    const qIds = usageByFileinfoId[fileinfo.id];
    gotoFilteredQuestionList(navigate, qIds, "File " + fileinfo.filename + " usage");
  };

  const [titleFilter, setTitleFilter] = useState("");
  if (titleFilter) {
    files = files.filter((f) => f.filename?.toLowerCase().includes(titleFilter.toLowerCase()));
  }
  const showFilter = files?.length > 5 || titleFilter; // hide for just-a-few-files

  /** @type {ColumnDef[]} */
  let columns = [
    {
      header: (
        <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
          <Text>Title</Text>
          {showFilter && (
            <Input
              borderRadius="md"
              borderColor={colors.esg_gray[300]}
              color={colors.esg_gray[300]}
              type="search"
              size="xs"
              value={titleFilter}
              onChange={(e) => setTitleFilter(e.target.value)}
            />
          )}
        </Box>
      ),
      accessorKey: "filename",
      cell: ({ row }) => <Text onClick={() => openSidebar(row.original)}>{row.original.filename}</Text>,
      size: 250,
    },
    {
      id: "download",
      size: 50,
      cell: ({ row }) => {
        return <FileDownloadButton fileInfo={row.original} />;
      },
    },
    {
      header: "Type",
      size: 75,
      accessorFn: (file) => getFileType(file),
      cell: ({ cell }) => <Text>{cell.getValue()}</Text>, // Note: Using Text for "normal" font -- Should that be the standard??
    },
    {
      header: "Upload date",
      size: 100,
      accessorFn: (file) => formatDate(file.createdDate),
      cell: ({ cell }) => <Text>{cell.getValue()}</Text>,
    },
    {
      id: "usage",
      header: "Usage",
      size: 75,
      accessorFn: (file) => usageByFileinfoId[file.id]?.length || "",
      cell: ({ row, cell }) => (
        <Text cursor="pointer" onClick={() => gotoUsage(row.original)}>
          {cell.getValue()}
        </Text>
      ),
    },
    {
      id: "aiDate",
      header: "AI",
      size: 50,
      accessorFn: (file) => formatDate(file.aiDate),
      cell: ({ cell }) => <span title={cell.getValue()}>{cell.getValue() ? <Check /> : ""}</span>,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        return (
          <Button float="right" variant="outline" onClick={() => openSidebar(row.original)}>
            {row.original.issues?.length}
            <CaretRight />
          </Button>
        );
      },
    },
  ];
  if (selectedIds) {
    columns.push({
      id: SELECTED_COLUMN_ID,
      header: "",
      size: 50,
      cell: ({ row }) => {
        return (
          <Checkbox
            colorScheme="limeShade"
            isChecked={selectedIds.includes(row.original.id)}
            onChange={(e) => {
              if (e.target.checked) {
                console.log("adding", row.original.id, "to", selectedIds);
                setSelectedIds([...selectedIds, row.original.id]);
              } else {
                console.log("removing", row.original.id, "from", selectedIds);
                setSelectedIds(selectedIds.filter((id) => id !== row.original.id));
              }
            }}
          />
        );
      },
    });
  }
  if (hideColumns) {
    columns = columns.filter((c) => !hideColumns.includes(c.id));
  }

  // Split by in/out of this q round
  let filesInRound = files?.filter((f) => f.folders?.includes(folder));
  let otherFiles = files?.filter((f) => !f.folders?.includes(folder));
  //   console.log("filesInRound", filesInRound, "from", files);

  const gotoUpload = () => {
    setRightSidebar(
      <SourcesUploadRSidebar activeFiles={filesInRound} hideAIReviewButton={hideAIReviewButton} tables={tables} />
    );
    doOpen();
  };

  const navigate = useNavigate();
  const gotoAIReview = () => {
    navigate(routes.aiReview);
  };

  return (
    <Box>
      <HStack justify="flex-end" mb={2}>
        {!hideAIReviewButton && (
          <Button variant="secondary" onClick={gotoAIReview}>
            AI Review <MagicWand ml={2} color={colors.navIconLime} />
          </Button>
        )}
        <Button variant="secondary" onClick={gotoUpload}>
          Add New <Upload ml={2} color={colors.navIconLime} />
        </Button>
      </HStack>

      {tables.includes("ACTIVE") && (
        <>
          {
            isLoading && (
              <LocalSpinner />
            ) /*TODO why doesn't this spinner show? Does it need explicit sizing or something? */
          }
          <H2 mb={4}>{roundId ? "Active" : "All Files"}</H2>
          {filesInRound && (
            <DataTableV2
              columns={columns}
              data={filesInRound}
              isLoading={isLoading}
              serverSidePagination={false} // TODO what happens if there are 1000 files?
              showPaginator={false} // TODO what happens if there are 1000 files?
              maxHeight="500px"
            />
          )}
        </>
      )}

      {roundId && <SourcesRoundsAsSourceSelector currentRoundId={roundId} selectedRounds={sourceRoundIds} setSelectedRounds={setSourceRoundIds} />}

      {roundId && tables.includes("OTHER") && (
        <>
          <H2 mt={8} mb={4}>
            Other
          </H2>
          {otherFiles && (
            <DataTableV2
              columns={columns.filter((c) => c.id !== SELECTED_COLUMN_ID)}
              data={otherFiles}
              isLoading={isLoading}
              serverSidePagination={false}
              showPaginator={false}
              maxHeight="400px"
            />
          )}
        </>
      )}

      {tables.includes(KB_FOLDER) && (
        <>
          <H2 mt={8} mb={4}>
            ESG360 Knowledge Base
          </H2>
          {isLoadingKB && <LocalSpinner />}
          {filesKBPage?.content && (
            <DataTableV2
              columns={columns}
              data={filesKBPage?.content}
              isLoading={isLoadingKB}
              serverSidePagination={false} // TODO what happens if there are 1000 files?
              showPaginator={false} // TODO what happens if there are 1000 files?
              maxHeight="400px"
            />
          )}
        </>
      )}

      {tables.includes("ARCHIVED") && (
        <>
          <H2 mt={8} mb={4}>
            Archived
          </H2>
          {isLoadingArchived && <LocalSpinner />}
          {filesArchivedPage?.content && (
            <DataTableV2
              columns={columns.filter((c) => c.id !== SELECTED_COLUMN_ID)}
              data={filesArchivedPage?.content}
              isLoading={isLoadingArchived}
              serverSidePagination={false} // TODO what happens if there are 1000 files?
              showPaginator={false} // TODO what happens if there are 1000 files?
              maxHeight="400px"
            />
          )}
        </>
      )}
    </Box>
  );
}

